import { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { REDEEM_CATEGORIES } from '@/data/rewardsData';
import { Sparkles } from 'lucide-react';

export const RedeemSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const [activeCategory, setActiveCategory] = useState(REDEEM_CATEGORIES[0].category);

  return (
    <section id="redeem" ref={ref} className="relative py-24 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Royal ornamental divider */}
        <div className="flex items-center gap-3 justify-center mb-10">
          <div className="h-px w-16 bg-gold/20" />
          <svg width="16" height="16" viewBox="0 0 20 20" className="text-gold/40">
            <path d="M10 0L12 8L20 10L12 12L10 20L8 12L0 10L8 8Z" fill="currentColor" />
          </svg>
          <div className="h-px w-16 bg-gold/20" />
        </div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16 lg:mb-20"
        >
          <span className="font-body text-xs tracking-[0.3em] uppercase text-gold mb-4 block">Redeem</span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-foreground font-medium leading-tight">
            Treat Yourself with <span className="text-shimmer-gold italic">Glow Points</span>
          </h2>
          <p className="font-body text-base text-muted-foreground mt-4 max-w-xl mx-auto leading-relaxed">
            Turn your loyalty into luxury. Redeem Glow Points for premium treatments across all categories.
          </p>
        </motion.div>

        {/* Tabs */}
        <Tabs defaultValue={REDEEM_CATEGORIES[0].category} onValueChange={setActiveCategory}>
          <TabsList className="flex justify-center gap-2 bg-transparent mb-10 flex-wrap">
            {REDEEM_CATEGORIES.map((cat) => (
              <TabsTrigger
                key={cat.category}
                value={cat.category}
                className="font-body text-sm px-5 py-2.5 rounded-full transition-all duration-300 data-[state=active]:bg-gold/15 data-[state=active]:text-gold data-[state=active]:border-gold/20 border border-transparent data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground bg-transparent"
              >
                {cat.category}
              </TabsTrigger>
            ))}
          </TabsList>

          {REDEEM_CATEGORIES.map((cat) => (
            <TabsContent key={cat.category} value={cat.category}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
                {cat.treatments.map((treatment, i) => (
                  <motion.div
                    key={treatment.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                  >
                    <Card className="bg-gradient-card border-border/40 rounded-xl p-6 hover:border-gold/20 transition-all duration-500 group flex flex-col">
                      <div className="flex items-start justify-between mb-4">
                        <h3 className="font-display text-lg text-foreground group-hover:text-gold transition-colors duration-300">{treatment.name}</h3>
                        <Sparkles className="w-4 h-4 text-gold/50 group-hover:text-gold transition-colors duration-300" />
                      </div>
                      <div className="flex items-end justify-between mt-auto pt-4 border-t border-border/30">
                        <div>
                          <span className="font-body text-xs text-muted-foreground block">Points Required</span>
                          <span className="font-body text-lg text-gold font-medium">{treatment.gp.toLocaleString()} GP</span>
                        </div>
                        <div className="text-right">
                          <span className="font-body text-xs text-muted-foreground block">Approx. Value</span>
                          <span className="font-body text-sm text-foreground">{treatment.value}</span>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};
